import { useCallback, useEffect, useMemo } from 'react'
import { LayoutDefinition } from '../dashboard/layout'
import { reconcileLayout } from '../dashboard/reconcileLayout'
import { PANELS, PanelId } from '../dashboard/registry'
import { WINDOW_DEFAULTS } from '../dashboard/windowDefaults'
import { PROVIDER_ROLES, ProviderRoleId } from '../currentRole'
import { makeLayoutControls } from './layoutControls'
import { readLocal, usePersistedState, writeLocal } from './localStore'
import { LayoutControls } from './useDashboardLayout'

const EMPTY: LayoutDefinition = { panels: [] }

const keyFor = (windowId: string, role: ProviderRoleId) => `advanced.window.${windowId}.${role}.v1`

// Pre-role shared layout key; read-only for migration.
const legacyKeyFor = (windowId: string) => `advanced.window.${windowId}.v1`

// Filter program-category panels on every read — stale ones crash window tabs.
export const isEligibleForWindow = (panelId: PanelId): boolean => PANELS[panelId].category !== 'program'

export const reconcileForWindow = (
  stored: { panels?: unknown } | null,
  windowId: string,
  fallback: LayoutDefinition = WINDOW_DEFAULTS[windowId] ?? EMPTY
): LayoutDefinition => {
  // `{ panels: [] }` is a deliberate clear — only `null`/missing means "never touched, use defaults."
  if (Array.isArray(stored?.panels) && stored.panels.length === 0) {
    return EMPTY
  }

  const reconciled = reconcileLayout(stored?.panels, fallback)

  return { panels: reconciled.panels.filter((entry) => isEligibleForWindow(entry.panel)) }
}

// EMPTY fallback so shipped WINDOW_DEFAULTS don't count as "has content".
export const hasWindowContent = (windowId: string, role: ProviderRoleId): boolean =>
  reconcileForWindow(readLocal(keyFor(windowId, role), null), windowId, EMPTY).panels.length > 0

export const clearWindowLayout = (windowId: string): void =>
  PROVIDER_ROLES.forEach(({ id }) => writeLocal(keyFor(windowId, id), EMPTY))

export type LocalWindowLayoutState = {
  layout: LayoutDefinition
  controls: LayoutControls
}

export const useLocalWindowLayout = (windowId: string, role: ProviderRoleId): LocalWindowLayoutState => {
  const [stored, setStored] = usePersistedState<{ panels?: unknown } | null>(keyFor(windowId, role), null)
  const layout = useMemo(() => reconcileForWindow(stored, windowId), [stored, windowId])

  // Migration off the pre-role shared key; psychiatrist only.
  useEffect(() => {
    if (role !== 'psychiatrist') {
      return
    }
    if (readLocal<{ panels?: unknown } | null>(keyFor(windowId, role), null)) {
      return
    }
    const legacy = readLocal<{ panels?: unknown } | null>(legacyKeyFor(windowId), null)
    if (legacy) {
      setStored(legacy)
    }
  }, [windowId, role, setStored])

  const apply = useCallback((next: LayoutDefinition) => setStored(next), [setStored])

  const controls = useMemo<LayoutControls>(() => makeLayoutControls(layout, apply, () => apply(EMPTY)), [apply, layout])

  return { layout, controls }
}
